/**
 * HOOK useEntityActivities — Timeline attività di un'entità, guidata dall'endpoint
 * BULK per-entità (TS-59). UNA sola GET (fetchEntityActivities) ritorna tutte le
 * attività collegate in proiezione leggera ActivitySummary[]; questo hook le mappa
 * ad ActivityItem[] per l'ActivityTimeline, sostituendo i 7 fan-out per-id per-tipo
 * (task/note/call/email/meeting/linkedin/message) che causavano l'N+1.
 *
 * Cosa resta CLIENT-SIDE (derivato dalle relazioni della propria sorgente nello
 * slice, caricate dalle detail page — 1 sola chiamata, non fa parte dell'N+1):
 *  - pin (relationshipId + pinned) via relByActivity
 *  - "primo contatto" dei task via taskFirstPersonId
 *  - "con {nomi}" via requestRecipients (lazy-on-expand, invariato)
 *
 * Lazy-on-expand: la lista porta solo la proiezione leggera; all'espansione di un
 * accordion il record COMPLETO (per l'edit body) si carica con la getX singola
 * (onExpandItem → requestFullRecord). I Message restano read-only, ma anch'essi
 * caricano on-expand: GET /message/{id}/summary porta il CORPO della mail, che
 * MessageActivityBody rende come HTML. Il corpo non sta nella proiezione bulk apposta —
 * sarebbero i corpi di tutte le mail dell'entità, comprese quelle mai aperte.
 *
 * `item.message` è però SEMPRE valorizzato (metadati dal bulk, corpo vuoto): l'accordion si monta
 * subito, ed è MessageActivityBody a leggere da `summariesById` se il corpo è arrivato. Legare la
 * presenza di `item.message` al caricamento sarebbe un errore: la timeline la usa anche per
 * decidere cosa mostrare da CHIUSO.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { htmlToPlainText, htmlToPlainTextPreserveBreaks } from '@crm/shared';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchTask } from '../entities/task/taskThunks';
import { fetchNote } from '../entities/note/noteThunks';
import { fetchCall } from '../entities/call/callThunks';
import { fetchEmail } from '../entities/email/emailThunks';
import { fetchMeeting } from '../entities/meeting/meetingThunks';
import { fetchLinkedinPost } from '../entities/linkedinPost/linkedinPostThunks';
import { fetchMessageSummary } from '../entities/message/messageThunks';
import { taskStatusFromDto } from '../entities/task/taskMappers';
import { fetchEntityActivities } from '../entities/activity/activityThunks';
import { isWhatsAppMediaType } from '../entities/whatsAppMessage/whatsAppMessageTypes';
import { fetchWhatsAppSessions } from '../entities/whatsAppSession/whatsAppSessionThunks';
import {
  entityActivitiesKey,
  type ActivitySummary,
  type ActivitySummaryType,
} from '../entities/activity/activityTypes';
import { personApi } from '../entities/person/personApi';
import { relationshipApi } from '../entities/relationship/relationshipApi';
import {
  selectRelationshipsBySource,
  selectRelationshipStatusBySource,
} from '../entities/relationship/relationshipSlice';
import { useRelationshipsSource } from '../entities/relationship/useRelationshipsSource';
import { useCurrentGroupId } from '../auth/authSelectors';
import { getEntityFqn } from '../entityRegistry/entityRegistry';
import type { MessageSummary } from '../entities/message/messageTypes';
import type { Task } from '../entities/task/taskTypes';
import type { Note } from '../entities/note/noteTypes';
import type { Call } from '../entities/call/callTypes';
import type { Email } from '../entities/email/emailTypes';
import type { Meeting } from '../entities/meeting/meetingTypes';
import type { LinkedinPost } from '../entities/linkedinPost/linkedinPostTypes';
import type { WhatsAppSession } from '../entities/whatsAppSession/whatsAppSessionTypes';
import type { Person } from '../entities/person/personTypes';
import { ACTIVITY_FILTER_TYPES } from './activitySections';
import type { ActivityItem, ActivityRecipient } from './ActivityTimeline';

// Filtri tipologia esposti dal hook ai consumer (ActivityTimeline). L'elenco e
// il suo ordine vengono da activitySections, che e' la fonte di verita' dei tipi:
// qui c'era una seconda lista scritta a mano, e le due potevano divergere.
// 'linkedin' resta fuori dai pill (filterable: false la'), come prima.
const ACTIVITY_FILTERS = ACTIVITY_FILTER_TYPES;

// ── Mapping ActivitySummary → ActivityItem (riga chiusa) ────────────────────
// Il type backend (union maiuscola) → tab della timeline. MESSAGE cade nel tab
// 'email' (le email importate compaiono accanto alle Email-attività).
const TYPE_TO_TAB: Record<ActivitySummaryType, ActivityItem['type']> = {
  TASK: 'task',
  NOTE: 'nota',
  CALL: 'chiamata',
  EMAIL: 'email',
  MEETING: 'riunione',
  LINKEDIN: 'linkedin',
  MESSAGE: 'email',
};

// Prefisso dell'id per-tipo: separa gli ID-space nel Set/Map di espansione
// (i taskId sono numerici "nudi" per compatibilità storica, gli altri prefissati).
const ID_PREFIX: Record<ActivitySummaryType, string> = {
  TASK: '',
  NOTE: 'note:',
  CALL: 'call:',
  EMAIL: 'email:',
  MEETING: 'meeting:',
  LINKEDIN: 'linkedin:',
  MESSAGE: 'message:',
};

function activityItemId(s: ActivitySummary): string | number {
  return s.type === 'TASK' ? s.classPK : `${ID_PREFIX[s.type]}${s.classPK}`;
}

/**
 * MessageSummary "leggera" per l'accordion, ricostruita dalla proiezione bulk: metadati sì, corpo
 * NO (il bulk non lo porta — sarebbero i corpi di tutte le mail dell'entità, anche mai aperte).
 * Serve a montare l'accordion SUBITO all'apertura, con direzione/data e i tool già interattivi:
 * il corpo arriva dopo, con la fetch on-expand, e nel frattempo MessageActivityBody mostra un
 * caricamento nella sola area del corpo.
 */
function toMessageSummary(s: ActivitySummary): MessageSummary {
  return {
    messageId: s.classPK,
    subject: s.title,
    // Corpo assente per costruzione: lo porta GET /message/{id}/summary all'espansione.
    body: '',
    // Il backend valorizza sempre direction per le attività MESSAGE; 'IN' è solo
    // fallback difensivo (MessageSummary.direction è obbligatorio).
    direction: s.direction ?? 'IN',
    receiptDate: s.date,
    senderName: s.userName,
    senderEmailAddress: '',
    // Allegati SÌ, al contrario del corpo: sono metadati, non il contenuto dei file, e chi li
    // mostra (la card "Allegati" della scheda preventivo) non aspetta che l'accordion si apra.
    attachments: s.attachments ?? [],
  };
}

/** Base ActivityItem dalla sola proiezione leggera (senza record pieno né pin/recipients). */
function summaryToActivityItem(s: ActivitySummary): ActivityItem {
  const item: ActivityItem = {
    id: activityItemId(s),
    type: TYPE_TO_TAB[s.type],
    title: s.title,
    user: s.userName,
    date: s.date,
    className: s.className,
    classPK: s.classPK,
  };
  if (s.type === 'TASK') {
    item.status = s.status !== undefined ? taskStatusFromDto(s.status) : undefined;
    item.expirationDate = s.expirationDate ?? undefined;
  }
  // MESSAGE: metadati subito (accordion montato all'istante); il CORPO lo carica
  // MessageActivityBody, che legge da state.messages.summariesById la proiezione completa
  // richiesta on-expand. Qui non si aspetta nulla.
  if (s.type === 'MESSAGE') {
    item.message = toMessageSummary(s);
  }
  return item;
}

// ── Mapper dal record COMPLETO (quando in cache byId, dopo la lazy-on-expand) ──
// Producono i campi di display dal record pieno + attaccano l'entità: usati al
// posto di summaryToActivityItem quando il record è caricato, così le modifiche
// inline (updateTask/updateCall/…) si riflettono SUBITO nella riga chiusa senza
// dover rifetchare il bulk. className/classPK li aggiunge il chiamante (baseFromFull).
const SNIPPET_MAX = 80;
function snippet(html: string): string {
  const text = htmlToPlainText(html);
  return text.length <= SNIPPET_MAX ? text : `${text.slice(0, SNIPPET_MAX)}…`;
}

function taskToActivityItem(task: Task): ActivityItem {
  return {
    id: task.taskId,
    type: 'task',
    title: task.title,
    detail: task.notes ? htmlToPlainText(task.notes) : undefined,
    user: task.userName,
    date: task.createDate,
    status: task.status,
    expirationDate: task.expirationDate,
    task,
  };
}

function noteToActivityItem(note: Note): ActivityItem {
  return {
    id: `note:${note.noteId}`,
    type: 'nota',
    title: note.title,
    detail: htmlToPlainText(note.description),
    user: note.userName,
    date: note.createDate,
    note,
  };
}

function callToActivityItem(call: Call): ActivityItem {
  return {
    id: `call:${call.callId}`,
    type: 'chiamata',
    title: snippet(call.description),
    detail: htmlToPlainTextPreserveBreaks(call.description),
    user: call.userName,
    date: call.callDate,
    call,
  };
}

function emailToActivityItem(email: Email): ActivityItem {
  return {
    id: `email:${email.emailId}`,
    type: 'email',
    title: snippet(email.description),
    detail: htmlToPlainTextPreserveBreaks(email.description),
    user: email.userName,
    date: email.emailDate,
    email,
  };
}

function meetingToActivityItem(meeting: Meeting): ActivityItem {
  return {
    id: `meeting:${meeting.meetingId}`,
    type: 'riunione',
    title: snippet(meeting.description),
    detail: htmlToPlainTextPreserveBreaks(meeting.description),
    user: meeting.userName,
    date: meeting.meetingDate,
    meeting,
  };
}

function linkedinPostToActivityItem(linkedinPost: LinkedinPost): ActivityItem {
  return {
    id: `linkedin:${linkedinPost.linkedinPostId}`,
    type: 'linkedin',
    title: snippet(linkedinPost.message),
    detail: htmlToPlainTextPreserveBreaks(linkedinPost.message),
    user: linkedinPost.userName,
    date: linkedinPost.activityDate,
    linkedinPost,
  };
}

// Mappa WhatsAppSession -> ActivityItem: un accordion per SESSIONE nella timeline
// (non per messaggio). Le sessioni sono caricate a parte per personId (non via
// l'aggregatore bulk, che non le conosce) e concatenate agli items.
// mediaLabel: titolo (accordion chiuso) usato quando l'ultimo messaggio è un media
// senza testo (es. "[MEDIA]").
/** FQN della sessione: chiave della relazione che porta il pin. */
export const WHATSAPP_SESSION_FQN = 'it.dvel.crm.core.model.WhatsAppSession';

/**
 * Il confronto per data decrescente delle attività, uno solo per l'hook e per la timeline.
 *
 * Una data mancante o non valida vale «la più vecchia» (FILO-48). Il confronto ingenuo,
 * `new Date(b.date) - new Date(a.date)`, su una data vuota dà `NaN`, che `sort` tratta come
 * «uguali»: e non sbaglia solo quell'elemento, rompe l'ordine anche fra quelli con la data —
 * il 20 ottobre finiva sopra il 25.
 */
export function compareActivityDateDesc(a: { date: string }, b: { date: string }): number {
  const time = (date: string) => {
    const value = new Date(date).getTime();
    return Number.isNaN(value) ? Number.NEGATIVE_INFINITY : value;
  };
  const left = time(a.date);
  const right = time(b.date);

  // Due date mancanti sono pari: -Infinity meno -Infinity sarebbe di nuovo NaN.
  return left === right ? 0 : right > left ? 1 : -1;
}

function whatsAppSessionToActivityItem(
  session: WhatsAppSession,
  mediaLabel: string,
): ActivityItem {
  const last =
    session.messages.length > 0
      ? session.messages[session.messages.length - 1]
      : undefined;
  // Titolo: il testo dell'ultimo messaggio; se è un media senza testo, il segnaposto.
  //
  // `isWhatsAppMediaType` e non `type !== 'TEXT'`: quest'ultimo è un insieme APERTO, e infatti
  // quando è arrivato TEMPLATE ci era finito dentro da solo. Il predicato per appartenenza è
  // l'unico posto che decide cosa sia un media — qui la regola non si riscrive, si usa.
  const title = last
    ? last.body || (isWhatsAppMediaType(last.type) ? mediaLabel : '')
    : '';
  return {
    id: `whatsapp:${session.whatsAppSessionId}`,
    type: 'whatsapp',
    title,
    detail: title,
    user: session.userName,
    date: session.lastMessageDate || session.startDate,
    whatsAppSession: session,
  };
}

export function useEntityActivities(entityType: string, entityId: number) {
  const dispatch = useAppDispatch();
  const groupId = useCurrentGroupId();
  const { t } = useTranslation();

  // FQN dell'entità e dei tipi attività (i record Redux/Relationship usano sempre FQN).
  const entityFqn = getEntityFqn(entityType);
  const taskFqn = getEntityFqn('Task');
  const noteFqn = getEntityFqn('Note');
  const callFqn = getEntityFqn('Call');
  const emailFqn = getEntityFqn('Email');
  const meetingFqn = getEntityFqn('Meeting');
  const linkedinPostFqn = getEntityFqn('LinkedinPost');
  const messageFqn = getEntityFqn('Message');
  const personFqn = getEntityFqn('Person');

  // Proiezioni bulk dell'entità corrente (source of truth della lista).
  const entityKey = entityFqn ? entityActivitiesKey(entityFqn, entityId) : '';
  const summaries = useAppSelector((state) =>
    entityKey ? state.activities.byEntity[entityKey] : undefined,
  );
  const activitiesStatus = useAppSelector((state) => state.activities.status);

  // Relationship dell'entità (per pin + primo contatto, client-side). Lette dal
  // bucket della propria sorgente: prima erano lo slice intero, e il filtro per
  // sorgente era rifatto a mano più sotto (relSignature) — con l'indicizzazione
  // arriva gratis, e `relStatus` smette di riflettere il caricamento altrui.
  // Dichiara il bisogno invece di darlo per scontato: prima la timeline
  // dipendeva dal fatto che la pagina ospite avesse fetchato per lei. L'hook e'
  // idempotente e ref-contato, quindi condividere la sorgente con i pannelli
  // relazione non raddoppia le chiamate.
  useRelationshipsSource(entityFqn, entityId);
  const allRelationships = useAppSelector(selectRelationshipsBySource(entityFqn, entityId));
  const relStatus = useAppSelector(selectRelationshipStatusBySource(entityFqn, entityId));

  // byId per l'ENRICHMENT del record pieno (lazy-on-expand): finché l'accordion è
  // chiuso questi non contengono l'attività, e l'item resta la sola proiezione.
  const tasksById = useAppSelector((state) => state.tasks.byId);
  const notesById = useAppSelector((state) => state.notes.byId);
  const callsById = useAppSelector((state) => state.calls.byId);
  const emailsById = useAppSelector((state) => state.emails.byId);
  const meetingsById = useAppSelector((state) => state.meetings.byId);
  const linkedinPostsById = useAppSelector((state) => state.linkedinPosts.byId);
  // Message: l'equivalente del "record pieno" è la proiezione con il corpo (GET /message/{id}
  // /summary), che vive in una cache sua — non in byId (là c'è il Message intero della smart inbox).
  const messageSummariesById = useAppSelector((state) => state.messages.summariesById);
  const whatsAppSessionsByPersonId = useAppSelector(
    (state) => state.whatsAppSessions.byPersonId,
  );

  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  // Cache nomi person per il "Primo contatto" (display-only, locale: non clobbera
  // state.persons.currentItem letto dalla Detail page).
  const [personNameById, setPersonNameById] = useState<Map<number, string>>(new Map());

  // Avvisa se l'entityType non è registrato (il fetch non partirebbe con FQN vuoto).
  useEffect(() => {
    if (!entityFqn) {
      console.warn(
        '[activities] useEntityActivities: entityType non registrato nel registry',
        { entityType },
      );
    }
  }, [entityType, entityFqn]);

  // ── FETCH BULK: una sola GET per entità, rifetchata quando cambiano i
  // link-attività (create). ─────────────────────────────────────────────────
  // Firma = insieme ordinato dei `${fqn}:${classPK}` delle attività collegate
  // (da allRelationships). Un create passa da onCreated→refreshRelations→
  // fetchRelationships, quindi la firma cresce e scatta il refetch. Il pin
  // (stessi link) e le relazioni non-attività (es. Person↔Person) NON la
  // cambiano. Il delete NON tocca le Relationship (gestito dal pruning dello
  // slice), quindi non passa di qui.
  const activityFqnSet = useMemo(
    () =>
      new Set(
        [taskFqn, noteFqn, callFqn, emailFqn, meetingFqn, linkedinPostFqn, messageFqn].filter(
          (f): f is string => Boolean(f),
        ),
      ),
    [taskFqn, noteFqn, callFqn, emailFqn, meetingFqn, linkedinPostFqn, messageFqn],
  );

  const relSignature = useMemo(() => {
    if (!entityFqn) return '';
    const keys: string[] = [];
    for (const r of allRelationships) {
      if (r.className1 === entityFqn && r.classPK1 === entityId && activityFqnSet.has(r.className2)) {
        keys.push(`${r.className2}:${r.classPK2}`);
      } else if (
        r.className2 === entityFqn && r.classPK2 === entityId && activityFqnSet.has(r.className1)
      ) {
        keys.push(`${r.className1}:${r.classPK1}`);
      }
    }
    return keys.sort().join(',');
  }, [allRelationships, entityFqn, entityId, activityFqnSet]);

  // Baseline della firma già fetchata; azzerata al cambio entità.
  const lastFetchedSigRef = useRef<string | null>(null);
  useEffect(() => {
    lastFetchedSigRef.current = null;
  }, [entityFqn, entityId]);

  useEffect(() => {
    if (!entityFqn || !groupId) return;
    // Attende le relazioni caricate (baseline della firma): evita il doppio
    // fetch iniziale (mount + popolamento relazioni async).
    if (relStatus !== 'succeeded') return;
    if (lastFetchedSigRef.current === relSignature) return;
    lastFetchedSigRef.current = relSignature;
    dispatch(fetchEntityActivities({ className: entityFqn, classPK: entityId }));
  }, [entityFqn, entityId, groupId, relStatus, relSignature, dispatch]);

  // Sessioni WhatsApp del contatto (coi messaggi annidati): caricate direttamente
  // per personId (non via l'aggregatore bulk né via Relationship). Solo Person.
  //
  // La guardia su `groupId` è la stessa dell'effect gemello qui sopra: senza, al primo
  // render (groupId non ancora nello store) partiva una GET /whatsapp-sessions/0/{id}
  // che falliva, lasciando lo slice in stato 'failed' per una chiamata mai dovuta.
  useEffect(() => {
    if (entityType !== 'Person' || !entityId || !groupId) return;
    dispatch(fetchWhatsAppSessions({ personId: entityId }));
  }, [entityType, entityId, groupId, dispatch]);

  const loading = activitiesStatus === 'loading' && summaries === undefined;

  // ── Primo contatto dei task (client-side, da allRelationships) ─────────────
  // NB: senza il record Task pieno non è disponibile il campo Task.type, quindi
  // NON si può escludere i task type='note' come faceva la versione per-id. Se il
  // filtro serve, aggiungere taskType (solo TASK) ad ActivitySummary.
  const taskClassPKs = useMemo(
    () => (summaries ?? []).filter((s) => s.type === 'TASK').map((s) => s.classPK),
    [summaries],
  );

  const taskFirstPersonId = useMemo(() => {
    const map = new Map<number, number>();
    if (!personFqn) return map;
    for (const taskId of taskClassPKs) {
      const candidates: { personId: number; createDate: string }[] = [];
      for (const r of allRelationships) {
        if (r.className1 === taskFqn && r.classPK1 === taskId && r.className2 === personFqn) {
          candidates.push({ personId: r.classPK2, createDate: r.createDate });
        } else if (r.className2 === taskFqn && r.classPK2 === taskId && r.className1 === personFqn) {
          candidates.push({ personId: r.classPK1, createDate: r.createDate });
        }
      }
      if (candidates.length > 0) {
        candidates.sort(
          (a, b) => new Date(a.createDate).getTime() - new Date(b.createDate).getTime(),
        );
        map.set(taskId, candidates[0].personId);
      }
    }
    return map;
  }, [taskClassPKs, allRelationships, taskFqn, personFqn]);

  // Fetch dei nomi person per il "primo contatto" (display-only, cache locale).
  useEffect(() => {
    const needed = new Set<number>(taskFirstPersonId.values());
    const missing = Array.from(needed).filter((id) => !personNameById.has(id));
    if (missing.length === 0) return;
    let cancelled = false;
    Promise.all(missing.map((id) => personApi.getById(id)))
      .then((persons: Person[]) => {
        if (cancelled) return;
        setPersonNameById((prev) => {
          const next = new Map(prev);
          for (const p of persons) next.set(p.personId, `${p.firstName} ${p.lastName}`.trim());
          return next;
        });
      })
      .catch(() => {
        /* nomi mancanti -> firstContactName undefined nei items. */
      });
    return () => {
      cancelled = true;
    };
  }, [taskFirstPersonId, personNameById]);

  // ── pin: relationshipId + pinned dal record entità-sorgente ────────────────
  // Solo il record forward (className1=entityFqn, classPK1=entityId) porta il
  // pinned reale (i reverse normalizzati lo forzano a false). Chiave `${fqn}:${id}`.
  const relByActivity = useMemo(() => {
    const map = new Map<string, { relationshipId: number; pinned: boolean }>();
    if (!entityFqn) return map;
    for (const r of allRelationships) {
      if (r.className1 === entityFqn && r.classPK1 === entityId) {
        map.set(`${r.className2}:${r.classPK2}`, {
          relationshipId: r.relationshipId,
          pinned: r.pinned,
        });
      }
    }
    return map;
  }, [allRelationships, entityFqn, entityId]);

  // ── "con {nomi}": lazy-on-expand, invariato ────────────────────────────────
  const [recipientsByItemId, setRecipientsByItemId] = useState<Map<string, ActivityRecipient[]>>(
    new Map(),
  );
  const requestedRef = useRef<Set<string>>(new Set());
  const genRef = useRef(0);

  // Cambiando entità: azzera cache/dedup e avanza la generazione (invalida i
  // fetch in volo dell'entità precedente — evita self calcolato sull'entità sbagliata).
  useEffect(() => {
    genRef.current += 1;
    requestedRef.current = new Set();
    setRecipientsByItemId(new Map());
    setPersonNameById(new Map());
  }, [entityFqn, entityId]);

  // Risolve i recipient di UNA attività appena espansa, dall'identità leggera
  // (className/classPK) dell'item. Task/note non hanno recipient; le riunioni CON
  // titolo backend sono escluse (solo se il record pieno è già in cache).
  const requestRecipients = useCallback(
    async (item: ActivityItem) => {
      if (!groupId || !personFqn) return;
      const fqn = item.className;
      const activityId = item.classPK;
      if (!fqn || activityId === undefined) return;

      const supported =
        fqn === emailFqn || fqn === callFqn || fqn === meetingFqn ||
        fqn === linkedinPostFqn || fqn === messageFqn;
      if (!supported) return;

      // Riunione con titolo backend: niente "con {nomi}" (verificabile solo se il
      // Meeting pieno è già stato caricato on-expand).
      if (fqn === meetingFqn && meetingsById[activityId]?.title?.trim()) return;

      const key = `${fqn}:${activityId}`;
      if (requestedRef.current.has(key)) return;
      requestedRef.current.add(key);
      const gen = genRef.current;
      try {
        const result = await relationshipApi.getRelationshipsFromContributors(
          groupId,
          fqn,
          activityId,
        );
        if (gen !== genRef.current) return;
        const personContributor = (result.contributors ?? []).find(
          (c) => c.className === personFqn,
        );
        const seen = new Set<number>();
        const recipients: ActivityRecipient[] = (personContributor?.entries ?? [])
          .filter((e) => !seen.has(e.classPK) && seen.add(e.classPK))
          .map((e) => ({
            personId: e.classPK,
            name: e.title,
            self: entityFqn === personFqn && e.classPK === entityId,
          }));
        setRecipientsByItemId((prev) => new Map(prev).set(key, recipients));
      } catch {
        requestedRef.current.delete(key);
      }
    },
    [groupId, personFqn, entityFqn, entityId, emailFqn, callFqn, meetingFqn, linkedinPostFqn,
      messageFqn, meetingsById],
  );

  // Lazy-on-expand del record COMPLETO (edit body). Dispatch della getX singola
  // per il tipo dell'item se non già in cache. Per il Message il "record" è la proiezione
  // con il corpo (fetchMessageSummary): resta read-only, ma il corpo HTML si carica qui.
  const requestFullRecord = useCallback(
    (item: ActivityItem) => {
      const fqn = item.className;
      const id = item.classPK;
      if (!fqn || id === undefined) return;
      if (fqn === taskFqn) {
        if (!tasksById[id]) dispatch(fetchTask(id));
      } else if (fqn === noteFqn) {
        if (!notesById[id]) dispatch(fetchNote(id));
      } else if (fqn === callFqn) {
        if (!callsById[id]) dispatch(fetchCall(id));
      } else if (fqn === emailFqn) {
        if (!emailsById[id]) dispatch(fetchEmail(id));
      } else if (fqn === meetingFqn) {
        if (!meetingsById[id]) dispatch(fetchMeeting(id));
      } else if (fqn === linkedinPostFqn) {
        if (!linkedinPostsById[id]) dispatch(fetchLinkedinPost(id));
      } else if (fqn === messageFqn) {
        if (!messageSummariesById[id]) dispatch(fetchMessageSummary(id));
      }
    },
    [dispatch, taskFqn, noteFqn, callFqn, emailFqn, meetingFqn, linkedinPostFqn, messageFqn,
      tasksById, notesById, callsById, emailsById, meetingsById, linkedinPostsById,
      messageSummariesById],
  );

  // Callback unica esposta ad ActivityTimeline.onExpandItem: fetch record pieno +
  // recipient. (Sostituisce l'uso di requestRecipients come onExpandItem.)
  const onExpandItem = useCallback(
    (item: ActivityItem) => {
      requestFullRecord(item);
      requestRecipients(item);
    },
    [requestFullRecord, requestRecipients],
  );

  // ── Derivazione finale items[] ─────────────────────────────────────────────
  const items = useMemo(() => {
    // Se il record COMPLETO è in cache (caricato on-expand), l'item ne deriva i
    // campi di display — così un edit inline si riflette subito. Altrimenti si
    // usa la proiezione leggera. className/classPK sempre dall'`ActivitySummary`.
    const baseFromFull = (s: ActivitySummary): ActivityItem | null => {
      const withId = (item: ActivityItem): ActivityItem =>
        ({ ...item, className: s.className, classPK: s.classPK });
      switch (s.type) {
        case 'TASK': { const r = tasksById[s.classPK]; return r ? withId(taskToActivityItem(r)) : null; }
        case 'NOTE': { const r = notesById[s.classPK]; return r ? withId(noteToActivityItem(r)) : null; }
        case 'CALL': { const r = callsById[s.classPK]; return r ? withId(callToActivityItem(r)) : null; }
        case 'EMAIL': { const r = emailsById[s.classPK]; return r ? withId(emailToActivityItem(r)) : null; }
        case 'MEETING': { const r = meetingsById[s.classPK]; return r ? withId(meetingToActivityItem(r)) : null; }
        case 'LINKEDIN': { const r = linkedinPostsById[s.classPK]; return r ? withId(linkedinPostToActivityItem(r)) : null; }
        default: return null; // MESSAGE: sempre dalla proiezione (il corpo lo legge l'accordion)
      }
    };

    const summaryItems = (summaries ?? [])
      .map((s) => {
        // record pieno se caricato (edit-live), altrimenti proiezione leggera
        let item: ActivityItem = baseFromFull(s) ?? summaryToActivityItem(s);
        // pin
        const rel = relByActivity.get(`${s.className}:${s.classPK}`);
        item = { ...item, relationshipId: rel?.relationshipId, pinned: rel?.pinned ?? false };
        // recipients "con {nomi}"
        const recipients = recipientsByItemId.get(`${s.className}:${s.classPK}`);
        if (recipients && recipients.length > 0) item = { ...item, recipients };
        // primo contatto (task)
        if (s.type === 'TASK') {
          const pid = taskFirstPersonId.get(s.classPK);
          const name = pid !== undefined ? personNameById.get(pid) : undefined;
          if (name) item = { ...item, firstContactName: name };
        }
        return item;
      });
    // WhatsApp: un item per SESSIONE (solo Person), non prodotto dall'aggregatore.
    const whatsAppSessionItems: ActivityItem[] =
      entityType === 'Person'
        ? (whatsAppSessionsByPersonId[entityId] ?? []).map((s) => {
            const item = whatsAppSessionToActivityItem(s, t('whatsapp.media.label'));
            // Stesso aggancio degli altri tipi: senza, il pin non si vedrebbe mai attivo
            // e "appunta" fallirebbe con "relazione non trovata". La riga la crea il
            // backend quando la sessione si lega al contatto; per le sessioni piu'
            // vecchie di quella modifica non c'e', e la crea il pin come fallback.
            const rel = relByActivity.get(`${WHATSAPP_SESSION_FQN}:${s.whatsAppSessionId}`);
            return { ...item, relationshipId: rel?.relationshipId, pinned: rel?.pinned ?? false };
          })
        : [];
    // Il backend ordina già per data desc; ri-ordino difensivo (stabile).
    return [...summaryItems, ...whatsAppSessionItems].sort(compareActivityDateDesc);
  }, [summaries, tasksById, notesById, callsById, emailsById, meetingsById, linkedinPostsById,
    relByActivity, recipientsByItemId, taskFirstPersonId, personNameById,
    whatsAppSessionsByPersonId, entityType, entityId, t]);

  return {
    items,
    loading,
    filters: ACTIVITY_FILTERS,
    activeFilters,
    setActiveFilters,
    onExpandItem,
    // requestRecipients resta esposto per compatibilità, ma i consumer dovrebbero
    // usare onExpandItem (che copre anche il fetch del record pieno).
    requestRecipients,
  };
}
